// Types principaux de l'application MomoCarrelage

// Export des types SaaS
export * from './subscription';

// Export des types Dépenses & Comptabilité
export * from './expense.types';

export interface Client {
  id: number; // ID en number pour correspondre au backend
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  ville: string;
  codePostal: string;
  // Champs Factur-X (conformité e-invoicing 2026)
  siren?: string; // SIREN (9 chiffres) ou SIRET (14 chiffres)
  codePaysIso?: string; // Code pays ISO (ex: 'FR', 'BE', etc.) - défaut 'FR'
  numTvaIntracom?: string; // Numéro de TVA intracommunautaire (ex: FR12345678901)
  createdAt: string;
  updatedAt: string;
}

// Types pour les Projets
export enum StatutProjet {
  EN_ATTENTE_DEVIS = 'EN_ATTENTE_DEVIS',
  DEVIS_ENVOYE = 'DEVIS_ENVOYE',
  DEVIS_ACCEPTE = 'DEVIS_ACCEPTE',
  EN_COURS = 'EN_COURS',
  TERMINE = 'TERMINE',
  ANNULE = 'ANNULE',
}

export interface Projet {
  id: number;
  titre: string;
  description?: string;
  adresseChantier: string;
  clientId: number;
  client?: Client;
  statut: StatutProjet;
  dateDebut?: string;
  dateFin?: string;
  montantTotal?: number;
  montantPaye?: number;
  dateCreation: string;
  dateModification: string;
}

export enum TypeLigneDevis {
  SURFACE = 'SURFACE',
  UNITAIRE = 'UNITAIRE', // Mètre linéaire
  PIECE = 'PIECE',       // Unité (pièce)
  FORFAIT = 'FORFAIT',
}

export interface LigneDevis {
  id: string;
  description: string;
  type: TypeLigneDevis;

  // Pour type SURFACE
  modeSaisieSurface?: 'dimensions' | 'direct'; // Mode de saisie: dimensions (L×l) ou direct (surface)
  longueur?: number; // en mètres
  largeur?: number; // en mètres
  surface?: number; // surface en m² (calculée ou saisie directement)
  avecPertes?: boolean; // +10% si activé
  tailleCarrelage?: string; // Taille du carrelage (ex: "30x30", "60x60", "80x80", etc.)

  // Pour tous les types
  quantite: number;
  prixUnitaire: number;
  total: number; // calculé
  unite?: string; // Unité de mesure (m², ml, pièce, kg, L, forfait, etc.)

  // Sous-lignes (consommables associés)
  sousLignes?: LigneDevis[]; // Lignes enfants (ex: colle et joint pour une ligne de carrelage)
  parentId?: string; // ID de la ligne parente si c'est une sous-ligne
  estSousLigne?: boolean; // Indicateur pour faciliter l'affichage
}

export enum StatutDevis {
  BROUILLON = 'BROUILLON',
  ENVOYE = 'ENVOYE',
  ACCEPTE = 'ACCEPTE',
  REFUSE = 'REFUSE',
}

export interface Devis {
  id: number; // ID en number (long du backend)
  numero: string; // ex: DEV-2024-001
  clientId: number; // ID en number
  clientNom?: string; // Nom complet du client (retourné par le backend pour affichage rapide)
  client?: Client; // Populated pour l'affichage détaillé
  projetId?: number; // ID du projet
  projetTitre?: string; // Titre du projet

  dateCreation?: string; // Pour compatibilité frontend
  dateEmission: string; // Nom utilisé par le backend
  dateValidite: string;
  statut: StatutDevis;

  lignes: LigneDevis[];

  // Calculs - tous requis pour éviter les undefined
  sousTotal: number; // Calculé côté frontend
  montantHT: number; // Renvoyé par le backend
  tauxTVA: number; // en pourcentage (ex: 20)
  montantTVA: number; // Calculé
  totalTTC: number; // Calculé

  // Remises
  typeRemise?: TypeRemise; // Type de remise (POURCENTAGE ou MONTANT_FIXE)
  valeurRemise?: number; // Valeur de la remise (% ou montant fixe)
  montantRemise?: number; // Montant calculé de la remise
  montantHTApresRemise?: number; // Montant HT après application de la remise

  // Statut de facturation (calculé et persisté côté backend)
  estFacture?: boolean; // true si une facture COMPLETE ou SOLDE existe pour ce devis

  // Métadonnées
  notes?: string;
  conditions?: string;
  signatureClient?: string; // Signature électronique en base64
  dateSignature?: string; // Date de la signature
  createdAt?: string;
  updatedAt?: string;
  dateModification?: string;
}

// Type pour l'authentification
export interface User {
  id: string;
  nom: string;
  prenom?: string;
  email: string;
  emailProfessionnel?: string; // Email utilisé pour l'envoi de devis et factures
  entreprise: string;
  siret?: string;
  adresse?: string;
  telephone?: string;
  logo?: string;
  // Champs Factur-X (conformité e-invoicing 2026)
  siren?: string; // SIREN (9 chiffres) ou SIRET (14 chiffres)
  codePaysIso?: string; // Code pays ISO (ex: 'FR', 'BE', etc.) - défaut 'FR'
  numTvaIntracom?: string; // Numéro de TVA intracommunautaire (ex: FR12345678901)
  isExemptFromTVA?: boolean; // Exonération de TVA (franchise en base) - détecté lors de l'inscription
  // Champs SaaS Multi-Métiers
  corpsMetier?: string; // Corps de métier de l'artisan
  stripeCustomerId?: string; // ID client Stripe
  subscriptionActive?: boolean; // Statut de l'abonnement
  subscriptionEndDate?: string; // Date de fin de l'abonnement
  trialEndDate?: string; // Date de fin de la période d'essai
  hasActiveAccess?: boolean; // Accès actif (abonnement ou essai)
  subscriptionTier?: 'ESSENTIEL' | 'PREMIUM'; // Niveau d'abonnement
  billingCycle?: 'MONTHLY' | 'YEARLY'; // Cycle de facturation
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

// Types pour les factures
export enum TypeFacture {
  ACOMPTE = 'ACOMPTE',
  SOLDE = 'SOLDE',
  COMPLETE = 'COMPLETE',
  AVOIR = 'AVOIR',
}

// Types pour les remises
export enum TypeRemise {
  POURCENTAGE = 'POURCENTAGE',
  MONTANT_FIXE = 'MONTANT_FIXE',
}

// Types pour les avoirs
export enum TypeAvoir {
  TOTAL = 'TOTAL',
  PARTIEL = 'PARTIEL',
}

// Types pour le paiement Stripe
export enum StripePaymentStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export enum StatutFacture {
  EN_ATTENTE = 'EMISE',
  PAYEE = 'PAYEE',
  ANNULEE = 'ANNULEE',
}

// Enums Factur-X (conformité e-invoicing 2026)
export enum TypeOperation {
  SERVICE = 'SERVICE',      // Prestation de service
  VENTE = 'VENTE',         // Vente de marchandises
  MIXTE = 'MIXTE',         // Service + Vente
}

export enum MoyenPaiement {
  VIREMENT = 'VIREMENT',
  CB = 'CB',                // Carte bancaire
  ESPECES = 'ESPECES',
  CHEQUE = 'CHEQUE',        // Ajouté pour compatibilité legacy
  AUTRE = 'AUTRE',
}

export interface Facture {
  id: number; // ID en number
  numero: string; // ex: FAC-2024-001
  devisId: number; // Lien vers le devis d'origine (number)
  devis?: Devis; // Populated pour l'affichage
  clientId: number; // ID en number
  clientNom?: string; // Nom complet du client (retourné par le backend pour affichage rapide)
  client?: Client;
  projetId?: number; // ID du projet
  projetTitre?: string; // Titre du projet

  dateEmission: string;
  dateEcheance: string;
  statut: StatutFacture;
  type: TypeFacture;

  // Pour les acomptes
  pourcentageAcompte?: number; // ex: 30 pour 30%
  montantAcompte?: number; // Montant de l'acompte

  // Montants
  montantHT: number;
  tauxTVA: number;
  montantTVA: number;
  montantTTC: number;

  // Remises
  typeRemise?: TypeRemise; // Type de remise (POURCENTAGE ou MONTANT_FIXE)
  valeurRemise?: number; // Valeur de la remise (% ou montant fixe)
  montantRemise?: number; // Montant calculé de la remise
  montantHTApresRemise?: number; // Montant HT après application de la remise

  // Pour les avoirs uniquement
  factureOriginaleId?: number; // ID de la facture originale (pour les avoirs)
  factureOriginaleNumero?: string; // Numéro de la facture originale (pour les avoirs)

  // Lignes de facture (pour les factures complètes et avoirs)
  lignes?: Array<{
    id: number;
    description: string;
    quantite: number;
    unite: string;
    prixUnitaireHT: number;
    montantTotalHT: number;
    tauxTVA: number;
    montantTVA: number;
    montantTotalTTC: number;
  }>;
  inalterableHash?: string; // Hash inaltérable pour conformité légale

  // Paiement Stripe
  stripePaymentIntentId?: string; // ID du PaymentIntent Stripe
  stripePaymentLinkId?: string; // ID du lien de paiement
  stripePaymentUrl?: string; // URL du lien de paiement
  stripePaymentStatus?: StripePaymentStatus; // Statut du paiement Stripe
  stripePaymentDate?: string; // Date du paiement réussi

  // Métadonnées
  notes?: string;
  conditions?: string;
  moyenPaiement?: MoyenPaiement | string; // Enum ou string pour compatibilité legacy
  datePaiement?: string; // Date du paiement effectif

  // Champs Factur-X (conformité e-invoicing 2026)
  typeOperation?: TypeOperation; // Type d'opération (SERVICE, VENTE, MIXTE)
  devise?: string; // Code devise ISO (ex: 'EUR', 'USD') - défaut 'EUR'

  createdAt: string;
  updatedAt: string;
}

// ========================================
// DTOs pour les requêtes API (Backend)
// ========================================

/**
 * DTO pour une sous-ligne de document (consommables)
 * Correspond à SousLigneDocumentDTO du backend
 */
export interface SousLigneDocumentDTO {
  description: string;
  quantite: number;
  unite: string; // ex: "kg"
  prixUnitaireHT: number;
  tauxTVA?: number;
  ordre?: number;
}

/**
 * DTO pour créer/modifier une ligne de document (Devis/Facture)
 * Correspond à LigneDocumentRequestDTO du backend
 */
export interface LigneDocumentRequestDTO {
  description: string;
  quantite: number;
  unite: string; // ex: "m²", "unité", "forfait"
  prixUnitaireHT: number;
  tauxTVA?: number;
  ordre?: number;
  sousLignes?: SousLigneDocumentDTO[]; // Sous-lignes (consommables)
}

/**
 * DTO pour créer un devis
 * Correspond à DevisRequestDTO du backend
 */
export interface DevisRequestDTO {
  projetId: number;
  dateEmission?: string; // Format: YYYY-MM-DD
  dateValidite?: string; // Format: YYYY-MM-DD
  statut?: StatutDevis;
  montantHT: number;
  tauxTVA?: number;
  typeRemise?: TypeRemise; // Type de remise
  valeurRemise?: number; // Valeur de la remise
  conditions?: string;
  notes?: string;
  lignes?: LigneDocumentRequestDTO[];
  signatureClient?: string; // Signature électronique en base64
  dateSignature?: string; // Date de la signature
}

/**
 * DTO pour créer un projet
 */
export interface ProjetRequestDTO {
  titre: string;
  description?: string;
  adresseChantier: string;
  clientId: number;
  statut?: StatutProjet;
  dateDebut?: string;
  dateFin?: string;
}

/**
 * DTO pour créer une facture d'acompte
 */
export interface AcompteRequest {
  devisId: number;
  pourcentageAcompte: number; // ex: 30 pour 30%
}

/**
 * DTO pour créer une facture de solde
 */
export interface SoldeRequest {
  projetId: number;
  devisId: number;
}

/**
 * DTO pour mettre à jour une facture
 */
export interface FactureRequestDTO {
  dateEcheance?: string;
  notes?: string;
  moyenPaiement?: string;
  typeRemise?: TypeRemise; // Type de remise
  valeurRemise?: number; // Valeur de la remise
}

/**
 * DTO pour créer un avoir
 * Correspond à AvoirRequestDTO du backend
 */
export interface AvoirRequestDTO {
  factureOriginaleId: number; // ID de la facture originale
  typeAvoir: TypeAvoir; // Type d'avoir (TOTAL ou PARTIEL)
  montantHTPartiel?: number; // Montant HT partiel (requis si typeAvoir = PARTIEL)
  motif: string; // Motif de l'avoir (obligatoire)
  notes?: string; // Notes additionnelles
}

// ========================================
// Types pour le Catalogue de Produits (Métier Carreleur)
// ========================================

/**
 * Produit du catalogue
 */
export interface ProduitCatalogue {
  id: number;
  designation: string;         // Ex: "Colle carrelage C2"
  uniteMesure: string;         // Ex: "kg", "m²", "ml", "unité"
  prixUnitaireHT: number;
  categorieTva: number;        // Ex: 20.0
  dateCreation?: string;
  dateModification?: string;
}

/**
 * DTO pour créer/modifier un produit catalogue
 */
export interface ProduitCatalogueRequestDTO {
  designation: string;
  uniteMesure: string;
  prixUnitaireHT: number;
  categorieTva: number;
}

/**
 * Consommables calculés automatiquement
 */
export interface ConsommablesCalcules {
  consommables: Array<{
    nom: string;
    quantite: number;      // quantité selon l'unité
    prixUnitaire: number;  // prix unitaire selon l'unité
    total: number;         // en €
    unite: string;         // unité de mesure (kg, ml, pièce, etc.)
  }>;
}

// ========================================
// Types pour le Catalogue d'Articles (V2 - Calculateur Technique)
// ========================================

/**
 * Unités de mesure pour les articles
 */
export enum UniteArticle {
  M2 = 'M2',           // Mètre carré (carrelage)
  KG = 'KG',           // Kilogramme (colle, joint en vrac)
  SAC = 'SAC',         // Sac (colle, joint en sac)
  FORFAIT = 'FORFAIT', // Forfait (main d'œuvre)
  ML = 'ML',           // Mètre linéaire
  UNITE = 'UNITE',     // Unité
}

/**
 * Type d'article (catégorie métier)
 */
export enum TypeArticle {
  CARRELAGE = 'CARRELAGE',
  COLLE = 'COLLE',
  JOINT = 'JOINT',
  PLINTHE = 'PLINTHE',
  MAIN_OEUVRE = 'MAIN_OEUVRE',
  AUTRE = 'AUTRE',
}

/**
 * Article du catalogue (nouvelle entité backend)
 */
export interface Article {
  id: number;
  ref: string;              // Référence article (ex: "COL-C2-25", "JT-FLEX-5")
  designation: string;      // Ex: "Colle carrelage C2 - Sac 25kg"
  unite: UniteArticle;      // Unité de mesure
  prixHT: number;           // Prix unitaire HT
  categorieTva: number;     // Ex: 20.0
  type?: TypeArticle;       // Type/catégorie de l'article
  conditionnement?: string; // Ex: "25kg", "5kg", "boîte de 10"
  dateCreation?: string;
  dateModification?: string;
}

/**
 * DTO pour créer/modifier un article
 */
export interface ArticleRequestDTO {
  ref: string;
  designation: string;
  unite: UniteArticle;
  prixHT: number;
  categorieTva: number;
}

/**
 * Inputs du calculateur technique de carrelage (envoyés au backend)
 */
export interface CalculateurInputs {
  surfaceChantier: number;      // en m²
  longueurCarreau: number;      // en cm
  largeurCarreau: number;       // en cm
  largeurJoint: number;         // en mm (défaut: 3mm)
  epaisseurCarreau: number;     // en mm (défaut: 9mm)
}

/**
 * DTO Request pour le calculateur (envoyé au backend)
 */
export interface CalculateurRequestDTO {
  surfaceChantier: number;      // en m²
  longueurCarreau: number;      // en cm
  largeurCarreau: number;       // en cm
  largeurJoint?: number;        // en mm (défaut backend: 3mm)
  epaisseurCarreau?: number;    // en mm (défaut backend: 9mm)
}

/**
 * Détail du calcul de colle (retourné par le backend)
 */
export interface CalculColleDTO {
  quantiteKg: number;           // kg total nécessaire
  nombreSacs: number;           // Nombre de sacs de 25kg
  article?: Article;            // Article trouvé dans le catalogue
  prixUnitaireHT?: number;      // Prix HT par sac
  prixTotalHT?: number;         // Prix total HT
  prixTotalTTC?: number;        // Prix total TTC
}

/**
 * Détail du calcul de joint (retourné par le backend)
 */
export interface CalculJointDTO {
  quantiteKg: number;           // kg total nécessaire
  nombreSacs: number;           // Nombre de sacs de 5kg
  article?: Article;            // Article trouvé dans le catalogue
  prixUnitaireHT?: number;      // Prix HT par sac
  prixTotalHT?: number;         // Prix total HT
  prixTotalTTC?: number;        // Prix total TTC
}

/**
 * Détail du calcul de carrelage (retourné par le backend)
 */
export interface CalculCarrelageDTO {
  surfaceReelle: number;        // m² avec marge de sécurité 10%
  nombreCarreaux?: number;      // Nombre de carreaux (si longueur/largeur fournis)
  article?: Article;            // Article trouvé dans le catalogue
  prixUnitaireHT?: number;      // Prix HT par m²
  prixTotalHT?: number;         // Prix total HT
  prixTotalTTC?: number;        // Prix total TTC
}

/**
 * Résultats du calculateur technique (retourné par le backend)
 */
export interface CalculateurResponseDTO {
  colle: CalculColleDTO;
  joint: CalculJointDTO;
  carrelage: CalculCarrelageDTO;
  totalHT?: number;             // Total général HT
  totalTTC?: number;            // Total général TTC
}

/**
 * DTO pour injecter les résultats du calculateur dans un devis (envoyé au backend)
 */
export interface InjectionDevisRequestDTO {
  devisId: number;                        // ID du devis cible
  calculateur: CalculateurResponseDTO;    // Résultats du calculateur à injecter
  ordreDepart?: number;                   // Position d'insertion (optionnel, défaut: fin)
}

/**
 * Réponse de l'injection dans le devis (retourné par le backend)
 */
export interface InjectionDevisResponseDTO {
  success: boolean;
  lignesAjoutees: number;       // Nombre de lignes ajoutées (normalement 3)
  message: string;              // Message de confirmation
}

/**
 * Résultats du calculateur technique (format legacy pour compatibilité)
 * @deprecated Utiliser CalculateurResponseDTO à la place
 */
export interface CalculateurResultats {
  // Carrelage
  surfaceReelle: number;        // m² avec marge de sécurité 10%
  nombreCarreaux?: number;      // Nombre de carreaux calculé

  // Colle
  quantiteColleKg: number;      // kg total nécessaire
  nombreSacsColle: number;      // Sacs de 25kg

  // Joint
  quantiteJointKg: number;      // kg total nécessaire
  nombreSacsJoint: number;      // Sacs de 5kg

  // Prix (si articles trouvés dans le catalogue)
  prixCarrelageHT?: number;
  prixColleHT?: number;
  prixJointHT?: number;
  totalHT?: number;
}

// ========================================
// DTOs pour le paiement Stripe
// ========================================

/**
 * DTO pour créer un lien de paiement Stripe
 */
export interface CreatePaymentLinkRequest {
  factureId: number;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * DTO de réponse après création d'un lien de paiement
 */
export interface CreatePaymentLinkResponse {
  factureId: number;
  factureNumero: string;
  paymentUrl: string;
  paymentLinkId: string;
  status: string;
}

/**
 * DTO de statut de paiement
 */
export interface PaymentStatusResponse {
  factureId: number;
  factureNumero: string;
  factureStatut: string;
  stripePaymentStatus: StripePaymentStatus | null;
  stripePaymentIntentId: string | null;
  stripePaymentDate: string | null;
  message: string;
}